// Advanced file parser that uses backend PDF processing for better accuracy
export const parseFile = async (file) => {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();
  
  try {
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      // Use backend PDF processing for accurate text extraction
      return await parsePDFWithBackend(file);
    } else if (fileType.startsWith('text/') || 
               fileType === 'application/msword' || 
               fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
               fileName.endsWith('.txt') || 
               fileName.endsWith('.doc') || 
               fileName.endsWith('.docx')) {
      // For text files and Word documents, read as text
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    } else {
      throw new Error('Unsupported file type. Please upload a PDF, Word document, or text file.');
    }
  } catch (error) {
    console.error('Error parsing file:', error);
    throw new Error(`Failed to parse ${fileName}. Please try copying and pasting your resume text instead.`);
  }
};

// Advanced PDF parsing using backend API for accurate text extraction
const parsePDFWithBackend = async (file) => {
  try {
    // Get backend URL from environment
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 
                      import.meta.env.REACT_APP_BACKEND_URL ||
                      'http://localhost:8001';
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    
    // Send to backend PDF extraction endpoint
    const response = await fetch(`${backendUrl}/api/pdf/extract-text`, {
      method: 'POST',
      body: formData
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      // Handle different error types with user-friendly messages
      if (response.status === 422) {
        const errorDetail = result.detail || result;
        throw new Error(
          `PDF Processing Error: ${errorDetail.reason || errorDetail.error}\n\n` +
          `💡 Suggestion: ${errorDetail.suggestion || 'Please try copying and pasting your resume text manually.'}`
        );
      } else if (response.status === 413) {
        throw new Error('PDF file is too large (max 10MB). Please use a smaller file or copy-paste the text manually.');
      } else if (response.status === 400) {
        throw new Error('Invalid PDF file. Please ensure you\'re uploading a valid PDF document.');
      } else {
        throw new Error(result.detail || 'PDF processing failed. Please try copying and pasting your resume text manually.');
      }
    }
    
    if (result.success && result.extracted_text) {
      console.log(`✅ PDF extraction successful: ${result.extraction_stats.filename}`);
      console.log(`📊 Quality Score: ${result.quality_score}%, Words: ${result.extraction_stats.word_count}`);
      
      return result.extracted_text;
    } else {
      throw new Error('No text could be extracted from the PDF. Please copy and paste your resume text manually.');
    }
    
  } catch (error) {
    console.error('Backend PDF parsing failed:', error);
    
    // If backend fails, provide helpful fallback message
    if (error.message.includes('fetch')) {
      throw new Error('Unable to process PDF - service temporarily unavailable. Please copy and paste your resume text manually.');
    }
    
    throw error;
  }
};

